export const MOBILE_COURSES = [
  {
    id: 'react-native-fundamentals',
    title: 'React Native Fundamentals',
    description: 'Build cross-platform mobile apps with React Native. Learn native components, navigation, and deployment.',
    level: 'beginner',
    totalDuration: '12-15 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['React experience', 'JavaScript fundamentals', 'Basic mobile development concepts'],
    learningOutcomes: [
      'Build cross-platform mobile applications',
      'Master React Native components and APIs',
      'Implement native device features',
      'Handle navigation and state management',
      'Deploy apps to App Store and Google Play',
      'Optimize app performance and user experience'
    ],
    modules: [
      {
        id: 'rn-setup',
        title: 'React Native Setup & Development Environment',
        description: 'Setting up React Native development environment and creating your first app',
        duration: '90 minutes',
        difficulty: 'beginner',
        objectives: [
          'Set up React Native development environment',
          'Create and run React Native projects',
          'Understand project structure and configuration',
          'Use React Native CLI and Expo'
        ],
        content: `# React Native Setup & Development

## Development Environment Setup

### React Native CLI
\`\`\`bash
# Install React Native CLI
npm install -g react-native-cli

# Create new project
npx react-native init MyFirstApp
cd MyFirstApp

# Run on iOS
npx react-native run-ios

# Run on Android
npx react-native run-android
\`\`\`

### Expo CLI (Recommended for beginners)
\`\`\`bash
# Install Expo CLI
npm install -g expo-cli

# Create new project
expo init MyFirstApp
cd MyFirstApp

# Start development server
expo start
\`\`\`

## Project Structure
\`\`\`
MyFirstApp/
├── App.js
├── package.json
├── babel.config.js
├── metro.config.js
├── android/
├── ios/
├── node_modules/
└── __tests__/
\`\`\`

## Your First React Native Component

\`\`\`javascript
// App.js
import React from 'react';
import {
  SafeAreaView,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  View,
  TouchableOpacity,
} from 'react-native';

const App = () => {
  const [count, setCount] = React.useState(0);

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="dark-content" />
      <ScrollView contentInsetAdjustmentBehavior="automatic">
        <View style={styles.body}>
          <Text style={styles.title}>Welcome to React Native!</Text>
          <Text style={styles.counter}>Count: {count}</Text>
          <TouchableOpacity
            style={styles.button}
            onPress={() => setCount(count + 1)}
          >
            <Text style={styles.buttonText}>Increment</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F4F6',
  },
  body: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
    textAlign: 'center',
  },
  counter: {
    fontSize: 18,
    marginBottom: 20,
  },
  button: {
    backgroundColor: '#007AFF',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
  },
  buttonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
});

export default App;
\`\`\`

## Core Components

### Essential React Native Components
\`\`\`javascript
import {
  View,        // Container component
  Text,        // Text display
  Image,       // Image display
  ScrollView,  // Scrollable container
  FlatList,    // Optimized list
  TouchableOpacity, // Touchable button
  TextInput,   // Text input field
  Modal,       // Modal overlay
  StatusBar,   // Status bar control
  SafeAreaView // Safe area handling
} from 'react-native';
\`\`\`

## Styling in React Native

\`\`\`javascript
// StyleSheet for optimized styles
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  text: {
    fontSize: 18,
    color: '#333',
    fontWeight: 'bold',
  },
  // Flexbox layout
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});
\`\`\``,
        practice: {
          task: 'Create a simple todo app with React Native that allows adding, viewing, and deleting tasks',
          starter: `import React, { useState } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  FlatList,
  StyleSheet,
} from 'react-native';

const TodoApp = () => {
  // Add your state and functions here
  
  return (
    <SafeAreaView style={styles.container}>
      {/* Add your UI components here */}
    </SafeAreaView>
  );
};

// Add your styles here
const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
});

export default TodoApp;`,
          hints: [
            'Use useState to manage todos array and input text',
            'Use FlatList to render the list of todos',
            'Implement add, delete, and toggle completion functions',
            'Style the components using StyleSheet',
            'Use TouchableOpacity for interactive elements'
          ]
        },
        quiz: {
          questions: [
            {
              id: 'rn-1',
              question: 'Which React Native component is used for creating scrollable lists with good performance?',
              options: ['ScrollView', 'FlatList', 'ListView', 'VirtualizedList'],
              correctAnswer: 1,
              difficulty: 'easy',
              explanation: 'FlatList is optimized for rendering large lists with good performance and memory efficiency.',
              points: 10
            }
          ],
          passThreshold: 80
        }
      }
    ],
    type: 'mobile'
  },
  {
    id: 'flutter-development',
    title: 'Flutter Development',
    description: 'Master cross-platform app development with Flutter and Dart. Build beautiful, native mobile applications.',
    level: 'intermediate',
    totalDuration: '10-12 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Object-oriented programming', 'Basic mobile development concepts', 'Understanding of widgets'],
    learningOutcomes: [
      'Master Dart programming language',
      'Build responsive Flutter applications',
      'Implement state management patterns',
      'Work with Flutter widgets and layouts',
      'Handle device features and APIs',
      'Deploy Flutter apps to multiple platforms'
    ],
    modules: [
      {
        id: 'dart-basics',
        title: 'Dart Programming Fundamentals',
        description: 'Learn Dart language fundamentals for Flutter development',
        duration: '120 minutes',
        difficulty: 'beginner',
        objectives: [
          'Master Dart syntax and language features',
          'Understand object-oriented programming in Dart',
          'Work with asynchronous programming',
          'Use Dart collections and generics'
        ],
        content: `# Dart Programming Fundamentals

## Dart Language Basics

### Variables and Data Types
\`\`\`dart
// Variable declarations
var name = 'Flutter';
String title = 'Mobile Development';
int count = 10;
double price = 29.99;
bool isActive = true;

// Constants
const String API_URL = 'https://api.example.com';
final DateTime now = DateTime.now();

// Nullable types
String? nullableName;
int? nullableAge;
\`\`\`

### Functions
\`\`\`dart
// Function declarations
String greetUser(String name, {int age = 0}) {
  return 'Hello \$name, you are \$age years old';
}

// Arrow functions
double calculateArea(double radius) => 3.14 * radius * radius;

// Higher-order functions
List<int> numbers = [1, 2, 3, 4, 5];
List<int> doubled = numbers.map((n) => n * 2).toList();
List<int> evens = numbers.where((n) => n % 2 == 0).toList();
\`\`\`

### Classes and Objects
\`\`\`dart
class User {
  String name;
  int age;
  String? email;

  // Constructor
  User(this.name, this.age, {this.email});

  // Named constructor
  User.guest() : name = 'Guest', age = 0;

  // Methods
  void greet() {
    print('Hello, I am \$name');
  }

  // Getters and setters
  bool get isAdult => age >= 18;

  set userEmail(String? email) {
    this.email = email?.toLowerCase();
  }
}

// Usage
User user = User('Alice', 25, email: 'alice@example.com');
User guest = User.guest();
\`\`\`

### Inheritance and Polymorphism
\`\`\`dart
abstract class Animal {
  String name;
  Animal(this.name);
  
  void makeSound(); // Abstract method
}

class Dog extends Animal {
  Dog(String name) : super(name);
  
  @override
  void makeSound() {
    print('\$name says Woof!');
  }
}

class Cat extends Animal {
  Cat(String name) : super(name);
  
  @override
  void makeSound() {
    print('\$name says Meow!');
  }
}
\`\`\`

## Asynchronous Programming

### Futures and Async/Await
\`\`\`dart
// Future function
Future<String> fetchUserData(int userId) async {
  // Simulate API call
  await Future.delayed(Duration(seconds: 2));
  return 'User data for ID: \$userId';
}

// Using async/await
Future<void> loadUserData() async {
  try {
    String userData = await fetchUserData(123);
    print(userData);
  } catch (error) {
    print('Error: \$error');
  }
}

// Handling multiple futures
Future<void> loadMultipleData() async {
  List<Future<String>> futures = [
    fetchUserData(1),
    fetchUserData(2),
    fetchUserData(3),
  ];
  
  List<String> results = await Future.wait(futures);
  results.forEach(print);
}
\`\`\`

### Streams
\`\`\`dart
// Stream creation
Stream<int> countStream() async* {
  for (int i = 1; i <= 5; i++) {
    await Future.delayed(Duration(seconds: 1));
    yield i;
  }
}

// Stream usage
void listenToStream() {
  countStream().listen(
    (data) => print('Received: \$data'),
    onError: (error) => print('Error: \$error'),
    onDone: () => print('Stream completed'),
  );
}
\`\`\``,
        practice: {
          task: 'Create a Dart class hierarchy for a simple e-commerce system with products, cart, and order management',
          starter: `// E-commerce system
abstract class Product {
  String name;
  double price;
  
  Product(this.name, this.price);
  
  // Add abstract methods here
}

class ShoppingCart {
  // Add cart implementation
}

// Implement your classes here`,
          hints: [
            'Create Product base class with concrete implementations',
            'Implement ShoppingCart with add/remove/calculate methods',
            'Use generics for type safety',
            'Implement proper error handling',
            'Use async methods for operations that might take time'
          ]
        },
        quiz: {
          questions: [
            {
              id: 'dart-1',
              question: 'What keyword is used to mark a function as asynchronous in Dart?',
              options: ['await', 'async', 'future', 'promise'],
              correctAnswer: 1,
              difficulty: 'easy',
              explanation: 'The async keyword marks a function as asynchronous, allowing the use of await inside it.',
              points: 10
            }
          ],
          passThreshold: 80
        }
      }
    ],
    type: 'mobile'
  }
];